import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// Patch 51 — global display-currency layer (presentation only). GBP is always Kadana's true billing
// currency; this only converts GBP amounts to an approximate local guide the user picks. The choice
// persists across pages/sessions in localStorage and applies platform-wide (public + workspace).
const CurrencyContext = createContext(null);
const STORAGE_KEY = "kadana_display_currency_v1";

export function CurrencyProvider({ children }) {
  const [currencies, setCurrencies] = useState([]);
  const [currency, setCurrencyState] = useState(() => localStorage.getItem(STORAGE_KEY) || "GBP");
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    api.get("/currencies").then(r => {
      setCurrencies(r.data.currencies || []);
      setLastSync(r.data.last_sync || null);
    }).catch(() => {});
  }, []);

  const setCurrency = (code) => {
    setCurrencyState(code);
    localStorage.setItem(STORAGE_KEY, code);
  };

  const active = useMemo(
    () => currencies.find(c => c.code === currency) || { code: "GBP", symbol: "\u00a3", rate: 1, name: "Pound Sterling" },
    [currencies, currency]);

  // Convert a GBP amount (major units) to the active display currency for presentation only.
  const format = (gbpAmount) => {
    if (gbpAmount == null || isNaN(gbpAmount)) return "";
    const rate = active.rate || 1;
    // Patch 67 Part 2 — a CONVERTED display figure always rounds UP to a whole unit (Math.ceil),
    // for EVERY currency, and shows zero decimals. Presentation only; never the real charge.
    const val = Math.ceil(gbpAmount * rate);
    const num = val.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${active.symbol} ${num}`;
  };

  const value = { currency, setCurrency, currencies, active, isGbp: currency === "GBP", lastSync, format };
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyContext) || {
    currency: "GBP", setCurrency: () => {}, currencies: [],
    active: { code: "GBP", symbol: "\u00a3", rate: 1, name: "Pound Sterling" },
    isGbp: true, lastSync: null, format: (a) => (a == null ? "" : `\u00a3 ${Math.ceil(a).toLocaleString("en-GB")}`),
  };
}
